import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { teamsApi } from '@/utils/api';
import type { Team } from '@/types';

interface TeamsState {
  teams: Team[];
  currentTeam: Team | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
  filters: {
    programId?: string;
    isActive?: boolean;
  };
}

function enrichTeamWithActiveMembers(team: any): Team {
  const assignments = team.resourceAssignments || [];
  return {
    ...team,
    activeMembers: assignments.map((a: any) => ({
      id: a.resource.id,
      firstName: a.resource.firstName,
      lastName: a.resource.lastName,
      resourceLevelCode: a.resource.resourceLevel?.code || 'Unknown',
      resourceLevelId: a.resource.resourceLevelId,
    })),
    activeResourceCount: assignments.length,
  };
}

const initialState: TeamsState = {
  teams: [],
  currentTeam: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  filters: {},
};

// Async thunks
export const fetchTeams = createAsyncThunk(
  'teams/fetchAll',
  async (params: { programId?: string; isActive?: boolean } | undefined, { rejectWithValue }) => {
    const response = await teamsApi.getAll(params);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch teams');
    }
    return response.data as { teams: Team[] } | Team[];
  }
);

export const fetchTeamById = createAsyncThunk(
  'teams/fetchById',
  async (id: string, { rejectWithValue }) => {
    const response = await teamsApi.getById(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch team');
    }
    return response.data as { team: Team } | Team;
  }
);

export const createTeam = createAsyncThunk(
  'teams/create',
  async (data: Partial<Team>, { rejectWithValue }) => {
    const response = await teamsApi.create(data);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to create team');
    }
    return response.data as { team: Team } | Team;
  }
);

export const updateTeam = createAsyncThunk(
  'teams/update',
  async ({ id, data }: { id: string; data: Partial<Team> }, { rejectWithValue }) => {
    const response = await teamsApi.update(id, data);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update team');
    }
    return response.data as { team: Team } | Team;
  }
);

export const deleteTeam = createAsyncThunk(
  'teams/delete',
  async (id: string, { rejectWithValue }) => {
    const response = await teamsApi.delete(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to delete team');
    }
    return id;
  }
);

const teamsSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<TeamsState['filters']>>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentTeam: (state, action: PayloadAction<Team | null>) => {
      state.currentTeam = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchTeams.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { teams?: any[] } | any[];
        const raw = Array.isArray(payload) ? payload : (payload.teams || []);
        state.teams = raw.map(enrichTeamWithActiveMembers);
      })
      .addCase(fetchTeams.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch by ID
      .addCase(fetchTeamById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTeamById.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        if (payload && 'team' in payload && payload.team) {
          state.currentTeam = payload.team as Team;
        } else {
          state.currentTeam = payload as Team | null;
        }
      })
      .addCase(fetchTeamById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createTeam.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createTeam.fulfilled, (state, action) => {
        state.isCreating = false;
        const payload = action.payload;
        let raw: any;
        if (payload && 'team' in payload) {
          raw = (payload as { team: any }).team;
        } else {
          raw = payload;
        }
        if (raw) {
          state.teams.unshift(enrichTeamWithActiveMembers(raw));
        }
      })
      .addCase(createTeam.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateTeam.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateTeam.fulfilled, (state, action) => {
        state.isUpdating = false;
        const payload = action.payload;
        let raw: any;
        if (payload && 'team' in payload) {
          raw = (payload as { team: any }).team;
        } else {
          raw = payload;
        }
        if (raw) {
          const team = enrichTeamWithActiveMembers(raw);
          const index = state.teams.findIndex(t => t.id === team.id);
          if (index !== -1) {
            state.teams[index] = team;
          }
          if (state.currentTeam?.id === team.id) {
            state.currentTeam = team;
          }
        }
      })
      .addCase(updateTeam.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteTeam.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.teams = state.teams.filter(t => t.id !== action.payload);
        if (state.currentTeam?.id === action.payload) {
          state.currentTeam = null;
        }
      })
      .addCase(deleteTeam.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  setFilters, 
  clearFilters, 
  setCurrentTeam 
} = teamsSlice.actions;

export default teamsSlice.reducer;